import type { CallDto, CampaignCallListPaginatedDto } from './apiService';
import type { CampaignCallsListArg } from './realtimeListsApi';

// REST pages and socket pushes can arrive out of order; the snapshot with the later updatedAt wins.
// Equal timestamps are accepted: sentiment/analysis changes are pushed without touching the call row.
export const pickFresher = (current: CallDto | undefined, incoming: CallDto): CallDto =>
  current && Date.parse(incoming.updatedAt) < Date.parse(current.updatedAt) ? current : incoming;

const includesIgnoringCase = (value: string | undefined, search: string) =>
  (value ?? '').toLowerCase().includes(search.toLowerCase());

/**
 * The filters of GET /v2/campaigns/:id/calls/paginated (call.repository), checked on the client.
 * One difference: the backend searches with ILIKE '%…%', so `%` and `_` typed into a search act as
 * wildcards there and as plain characters here.
 */
export const matchesCallsFilter = (call: CallDto, arg: CampaignCallsListArg): boolean => {
  if (arg.statuses && call.status !== arg.statuses) return false;
  if (arg.types && call.type !== arg.types) return false;
  if (arg.endedReason && call.endedReason !== arg.endedReason) return false;
  if (arg.numberSearch && !includesIgnoringCase(call.customer?.number, arg.numberSearch)) {
    return false;
  }
  if (arg.callIdSearch && !includesIgnoringCase(call.id, arg.callIdSearch)) return false;

  if (arg.start || arg.end) {
    // A call that has not started yet has no startedAt and is outside any period
    if (!call.startedAt) return false;
    const startedAt = Date.parse(call.startedAt);
    if (arg.start && startedAt < Date.parse(arg.start)) return false;
    if (arg.end && startedAt > Date.parse(arg.end)) return false;
  }

  return true;
};

type SortField = NonNullable<CampaignCallsListArg['sortBy']>;

/**
 * The sort values of call.repository (pagination/sort.ts) that the client can compute, with the same
 * COALESCE fallbacks. The phone number compares by code unit, which matches the database collation
 * for numbers made of digits and '+'.
 */
const CLIENT_SORT_VALUES: Partial<Record<SortField, (call: CallDto) => number | string>> = {
  createdAt: (call) => Date.parse(call.createdAt),
  startDate: (call) => Date.parse(call.startedAt ?? call.createdAt),
  endedAt: (call) => Date.parse(call.endedAt ?? call.createdAt),
  phoneNumber: (call) => call.customer?.number ?? '',
};

// The backend breaks ties by id in the same direction
const createSort = (arg: CampaignCallsListArg) => {
  const sortValue = CLIENT_SORT_VALUES[arg.sortBy ?? 'createdAt'];
  if (!sortValue) return null;

  const direction = (arg.sortOrder ?? 'DESC') === 'DESC' ? -1 : 1;
  const compare = (a: CallDto, b: CallDto) => {
    const valueA = sortValue(a);
    const valueB = sortValue(b);
    if (valueA !== valueB) return valueA < valueB ? -1 : 1;
    if (a.id === b.id) return 0;
    return a.id < b.id ? -1 : 1;
  };

  return {
    sortValue,
    comesBefore: (call: CallDto, row: CallDto) => compare(call, row) * direction < 0,
  };
};

interface CallPages {
  pages: CampaignCallListPaginatedDto[];
}

/**
 * - `applied`: the cached pages show the call as the backend would.
 * - `reload`: the list is sorted by a value only the backend computes (duration, sentiment), so the
 *   call may now sit elsewhere, or a new call has no known place: the list needs a reload.
 */
export type CallPushOutcome = 'applied' | 'reload';

const changeTotal = (draft: CallPages, delta: number) => {
  const meta = draft.pages[0]?.meta;
  if (meta && typeof meta.totalItems === 'number') {
    meta.totalItems = Math.max(0, meta.totalItems + delta);
  }
};

const isListComplete = (draft: CallPages) => !draft.pages[draft.pages.length - 1]?.meta.hasMore;

const isListEmpty = (draft: CallPages) => draft.pages.every((page) => page.data.length === 0);

// Where the sort puts the call among the loaded rows, or null when it belongs to a page not loaded
const findPosition = (
  draft: CallPages,
  call: CallDto,
  comesBefore: (call: CallDto, row: CallDto) => boolean,
) => {
  for (const [pageIndex, page] of draft.pages.entries()) {
    const rowIndex = page.data.findIndex((row) => comesBefore(call, row));
    if (rowIndex !== -1) return { pageIndex, rowIndex };
  }

  const lastPageIndex = draft.pages.length - 1;
  if (lastPageIndex < 0 || !isListComplete(draft)) return null;
  return { pageIndex: lastPageIndex, rowIndex: draft.pages[lastPageIndex].data.length };
};

/**
 * Applies a pushed call to the cached pages, as the backend would list it:
 * - a snapshot older than the cached one is ignored;
 * - a cached call that no longer matches the filter is dropped, with the total;
 * - a matching call (cached or new) goes where the sort puts it; a new one adds to the total.
 * The same call can sit on two pages when rows shifted between requests: every copy is handled.
 * A push carries the call and its customer only, so fields it lacks (agent, squad) are kept.
 */
export const upsertCallInPages = (
  draft: CallPages,
  call: CallDto,
  arg: CampaignCallsListArg,
): CallPushOutcome => {
  const copies = draft.pages.flatMap((page, pageIndex) =>
    page.data.flatMap((row, rowIndex) =>
      row.id === call.id ? [{ pageIndex, rowIndex, row }] : [],
    ),
  );
  const cached = copies.reduce<CallDto | undefined>(
    (fresher, { row }) => pickFresher(fresher, row),
    undefined,
  );

  if (cached && pickFresher(cached, call) !== call) return 'applied';

  const matches = matchesCallsFilter(call, arg);
  const sort = createSort(arg);
  const row = cached ? { ...cached, ...call } : call;
  const first = copies[0];

  // Removed from the end, so the indexes of the copies before stay valid
  copies
    .slice()
    .reverse()
    .forEach(({ pageIndex, rowIndex }) => draft.pages[pageIndex].data.splice(rowIndex, 1));

  if (!matches) {
    if (cached) changeTotal(draft, -1);
    return 'applied';
  }

  if (!sort) {
    // Only the backend knows the order: keep a cached call where it was, until the reload
    if (first) {
      draft.pages[first.pageIndex].data.splice(first.rowIndex, 0, row);
      return 'reload';
    }
    if (isListEmpty(draft) && isListComplete(draft) && draft.pages[0]) {
      draft.pages[0].data.push(row);
      changeTotal(draft, 1);
      return 'applied';
    }
    return 'reload';
  }

  // Same sort value: it stays where the backend put it
  if (cached && first && sort.sortValue(cached) === sort.sortValue(row)) {
    draft.pages[first.pageIndex].data.splice(first.rowIndex, 0, row);
    return 'applied';
  }

  const position = findPosition(draft, row, sort.comesBefore);
  if (position) {
    draft.pages[position.pageIndex].data.splice(position.rowIndex, 0, row);
    if (!cached) changeTotal(draft, 1);
    return 'applied';
  }

  // Past every loaded row while more pages exist. A new call comes with its page; a cached one
  // stays visible at the end of what is loaded rather than vanish
  const lastPage = draft.pages[draft.pages.length - 1];
  if (cached && lastPage) lastPage.data.push(row);
  return 'applied';
};

// Pages from different requests can repeat a row when rows shift between them
export const flattenCallPages = (pages: CampaignCallListPaginatedDto[]): CallDto[] => {
  const uniqueById = new Map<string, CallDto>();
  pages.forEach(({ data }) =>
    data.forEach((item) => uniqueById.set(item.id, pickFresher(uniqueById.get(item.id), item))),
  );
  return Array.from(uniqueById.values());
};
